"use client";

import { authorizedFetch } from "@/lib/auth/authorized-fetch";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    createPrismSession,
    defaultProjectSteps,
    DEFAULT_PRISM_UI_STATE,
    PRISM_STATE_BLOB_TYPE,
    type FocusDockPosition,
    type PrismChatMessage,
    type PrismChatSession,
    type PrismCustomSkill,
    type PrismProject,
    type PrismProjectStep,
    type PrismUiState,
} from "@/lib/prism/session-types";
import type { PrismModelId } from "@/lib/prism/catalog";
import type { PrismScope } from "@/lib/prism/types";

function migrateSession(raw: unknown): PrismChatSession | null {
    if (!raw || typeof raw !== "object") return null;
    const s = raw as Partial<PrismChatSession> & { bookmarked?: boolean };
    if (!s.id) return null;
    return {
        id: s.id,
        title: s.title ?? "New chat",
        messages: Array.isArray(s.messages) ? s.messages : [],
        pinned: Boolean(s.pinned ?? s.bookmarked ?? false),
        lastUpdated: typeof s.lastUpdated === "number" ? s.lastUpdated : Date.now(),
        projectId: s.projectId ?? null,
    };
}

function migrateProject(raw: unknown): PrismProject | null {
    if (!raw || typeof raw !== "object") return null;
    const p = raw as Partial<PrismProject>;
    if (!p.id || !p.name) return null;
    return {
        id: p.id,
        name: p.name,
        description: p.description,
        createdAt: typeof p.createdAt === "number" ? p.createdAt : Date.now(),
        steps: Array.isArray(p.steps) ? p.steps : [],
    };
}

function mergeState(raw: unknown): PrismUiState {
    if (!raw || typeof raw !== "object") return { ...DEFAULT_PRISM_UI_STATE };
    const o = raw as Partial<PrismUiState>;
    const validModels: PrismModelId[] = ["flash", "pro", "premium"];
    return {
        schemaVersion: 1,
        dockPosition: o.dockPosition ?? DEFAULT_PRISM_UI_STATE.dockPosition,
        focusCollapsed: o.focusCollapsed ?? false,
        scope: o.scope === "company" ? "company" : "fund",
        activeSessionId: o.activeSessionId ?? null,
        sessions: Array.isArray(o.sessions)
            ? o.sessions.map(migrateSession).filter((s): s is PrismChatSession => s !== null)
            : [],
        selectedModelId: validModels.includes(o.selectedModelId as PrismModelId)
            ? (o.selectedModelId as PrismModelId)
            : "pro",
        customSkills: Array.isArray(o.customSkills) ? o.customSkills : [],
        projects: Array.isArray(o.projects)
            ? o.projects.map(migrateProject).filter((p): p is PrismProject => p !== null)
            : [],
    };
}

export function usePrismState() {
    const [state, setState] = useState<PrismUiState>(DEFAULT_PRISM_UI_STATE);
    const [hydrated, setHydrated] = useState(false);
    /** Which project's pipeline tab is open; null = chat view. (Not persisted.) */
    const [openProjectId, setOpenProjectId] = useState<string | null>(null);
    const syncRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const res = await authorizedFetch(
                    `/api/cloud/portfolio-blobs?blobType=${encodeURIComponent(PRISM_STATE_BLOB_TYPE)}`,
                    { credentials: "include" },
                );
                if (res.ok) {
                    const data = (await res.json()) as { payload?: unknown };
                    if (!cancelled && data.payload) {
                        setState(mergeState(data.payload));
                    }
                }
            } finally {
                if (!cancelled) setHydrated(true);
            }
        }
        void load();
        return () => {
            cancelled = true;
        };
    }, []);

    const persist = useCallback((next: PrismUiState) => {
        if (syncRef.current) clearTimeout(syncRef.current);
        syncRef.current = setTimeout(() => {
            void authorizedFetch("/api/cloud/portfolio-blobs", {
                method: "POST",
                credentials: "include",
                headers: { "content-type": "application/json" },
                body: JSON.stringify({
                    blobType: PRISM_STATE_BLOB_TYPE,
                    payload: next,
                }),
            });
        }, 400);
    }, []);

    const updateState = useCallback(
        (patch: Partial<PrismUiState> | ((prev: PrismUiState) => PrismUiState)) => {
            setState((prev) => {
                const next =
                    typeof patch === "function"
                        ? patch(prev)
                        : { ...prev, ...patch };
                persist(next);
                return next;
            });
        },
        [persist],
    );

    const activeSession = useMemo(() => {
        if (!state.activeSessionId) return null;
        return state.sessions.find((s) => s.id === state.activeSessionId) ?? null;
    }, [state.activeSessionId, state.sessions]);

    /** All sessions, pinned first, then by recency. */
    const visibleSessions = useMemo(() => {
        return [...state.sessions].sort((a, b) => {
            if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
            return b.lastUpdated - a.lastUpdated;
        });
    }, [state.sessions]);

    const ensureActiveSession = useCallback(() => {
        if (state.activeSessionId && state.sessions.some((s) => s.id === state.activeSessionId)) {
            return state.activeSessionId;
        }
        const session = createPrismSession();
        updateState((prev) => ({
            ...prev,
            activeSessionId: session.id,
            sessions: [session, ...prev.sessions],
        }));
        return session.id;
    }, [state.activeSessionId, state.sessions, updateState]);

    const newSession = useCallback(
        (projectId: string | null = null) => {
            const session = createPrismSession("New chat", projectId);
            updateState((prev) => ({
                ...prev,
                activeSessionId: session.id,
                sessions: [session, ...prev.sessions],
            }));
            setOpenProjectId(null);
            return session.id;
        },
        [updateState],
    );

    const selectSession = useCallback(
        (id: string) => {
            updateState({ activeSessionId: id });
            setOpenProjectId(null);
        },
        [updateState],
    );

    const deleteSession = useCallback(
        (id: string) => {
            updateState((prev) => {
                const sessions = prev.sessions.filter((s) => s.id !== id);
                let activeSessionId = prev.activeSessionId;
                if (activeSessionId === id) {
                    activeSessionId = sessions[0]?.id ?? null;
                }
                return { ...prev, sessions, activeSessionId };
            });
        },
        [updateState],
    );

    const togglePin = useCallback(
        (id: string) => {
            updateState((prev) => ({
                ...prev,
                sessions: prev.sessions.map((s) =>
                    s.id === id ? { ...s, pinned: !s.pinned } : s,
                ),
            }));
        },
        [updateState],
    );

    const appendMessages = useCallback(
        (sessionId: string, messages: PrismChatMessage[], title?: string) => {
            updateState((prev) => ({
                ...prev,
                sessions: prev.sessions.map((s) => {
                    if (s.id !== sessionId) return s;
                    return {
                        ...s,
                        title: title ?? s.title,
                        messages: [...s.messages, ...messages],
                        lastUpdated: Date.now(),
                    };
                }),
            }));
        },
        [updateState],
    );

    const setDockPosition = useCallback(
        (dockPosition: FocusDockPosition) => {
            updateState({ dockPosition, focusCollapsed: false });
        },
        [updateState],
    );

    const setFocusCollapsed = useCallback(
        (focusCollapsed: boolean) => {
            updateState({ focusCollapsed });
        },
        [updateState],
    );

    const setScope = useCallback(
        (scope: PrismScope) => {
            updateState({ scope });
        },
        [updateState],
    );

    const setModel = useCallback(
        (selectedModelId: PrismModelId) => {
            updateState({ selectedModelId });
        },
        [updateState],
    );

    const addCustomSkill = useCallback(
        (skill: Omit<PrismCustomSkill, "id" | "createdAt">) => {
            const created: PrismCustomSkill = {
                ...skill,
                id: crypto.randomUUID(),
                createdAt: Date.now(),
            };
            updateState((prev) => ({ ...prev, customSkills: [created, ...prev.customSkills] }));
            return created;
        },
        [updateState],
    );

    const deleteCustomSkill = useCallback(
        (id: string) => {
            updateState((prev) => ({
                ...prev,
                customSkills: prev.customSkills.filter((s) => s.id !== id),
            }));
        },
        [updateState],
    );

    const addProject = useCallback(
        (project: { name: string; description?: string; steps?: PrismProjectStep[] }) => {
            const created: PrismProject = {
                name: project.name,
                description: project.description,
                id: crypto.randomUUID(),
                createdAt: Date.now(),
                steps: project.steps ?? defaultProjectSteps(),
            };
            updateState((prev) => ({ ...prev, projects: [created, ...prev.projects] }));
            return created;
        },
        [updateState],
    );

    const updateProject = useCallback(
        (id: string, patch: Partial<Pick<PrismProject, "name" | "description">>) => {
            updateState((prev) => ({
                ...prev,
                projects: prev.projects.map((p) => (p.id === id ? { ...p, ...patch } : p)),
            }));
        },
        [updateState],
    );

    const deleteProject = useCallback(
        (id: string) => {
            updateState((prev) => ({
                ...prev,
                projects: prev.projects.filter((p) => p.id !== id),
                sessions: prev.sessions.map((s) =>
                    s.projectId === id ? { ...s, projectId: null } : s,
                ),
            }));
            setOpenProjectId((cur) => (cur === id ? null : cur));
        },
        [updateState],
    );

    const addProjectStep = useCallback(
        (projectId: string, step: { title: string; skillId?: string }) => {
            const created: PrismProjectStep = {
                id: crypto.randomUUID(),
                title: step.title,
                skillId: step.skillId,
                status: "todo",
            };
            updateState((prev) => ({
                ...prev,
                projects: prev.projects.map((p) =>
                    p.id === projectId ? { ...p, steps: [...p.steps, created] } : p,
                ),
            }));
        },
        [updateState],
    );

    const updateProjectStep = useCallback(
        (projectId: string, stepId: string, patch: Partial<Pick<PrismProjectStep, "title" | "status">>) => {
            updateState((prev) => ({
                ...prev,
                projects: prev.projects.map((p) =>
                    p.id === projectId
                        ? {
                              ...p,
                              steps: p.steps.map((s) => (s.id === stepId ? { ...s, ...patch } : s)),
                          }
                        : p,
                ),
            }));
        },
        [updateState],
    );

    const deleteProjectStep = useCallback(
        (projectId: string, stepId: string) => {
            updateState((prev) => ({
                ...prev,
                projects: prev.projects.map((p) =>
                    p.id === projectId
                        ? { ...p, steps: p.steps.filter((s) => s.id !== stepId) }
                        : p,
                ),
            }));
        },
        [updateState],
    );

    const assignSessionToProject = useCallback(
        (sessionId: string, projectId: string | null) => {
            updateState((prev) => ({
                ...prev,
                sessions: prev.sessions.map((s) =>
                    s.id === sessionId ? { ...s, projectId } : s,
                ),
            }));
        },
        [updateState],
    );

    /** Start a new chat inside a project and switch to the chat view. */
    const newSessionInProject = useCallback(
        (projectId: string, title?: string, keepProjectOpen?: boolean) => {
            const session = createPrismSession(title ?? "New chat", projectId);
            updateState((prev) => ({
                ...prev,
                activeSessionId: session.id,
                sessions: [session, ...prev.sessions],
            }));
            if (!keepProjectOpen) setOpenProjectId(null);
            return session.id;
        },
        [updateState],
    );

    const openProject = useCallback((id: string) => setOpenProjectId(id), []);
    const closeProject = useCallback(() => setOpenProjectId(null), []);

    const openProject_ = useMemo(
        () => state.projects.find((p) => p.id === openProjectId) ?? null,
        [state.projects, openProjectId],
    );

    return {
        hydrated,
        state,
        activeSession,
        visibleSessions,
        openProjectId,
        activeProject: openProject_,
        ensureActiveSession,
        newSession,
        selectSession,
        deleteSession,
        togglePin,
        appendMessages,
        setDockPosition,
        setFocusCollapsed,
        setScope,
        setModel,
        addCustomSkill,
        deleteCustomSkill,
        addProject,
        updateProject,
        deleteProject,
        addProjectStep,
        updateProjectStep,
        deleteProjectStep,
        assignSessionToProject,
        newSessionInProject,
        openProject,
        closeProject,
    };
}

export type UsePrismStateReturn = ReturnType<typeof usePrismState>;
